import { addDays } from 'date-fns';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  checkAvailability,
  setCanBeBooked,
  setDates,
  setError,
  toggleCalendarModal,
} from 'shared/actions/dailyrentBookingCalendar';
import { selectOfferId } from 'shared/selectors';
import {
  selectDailyrentBookingCalendarAvailabilityLoading,
  selectDailyrentBookingCalendarDates,
  selectDailyrentBookingCalendarDaysAvailability,
  selectDailyrentBookingCalendarInitialLoading,
  selectDailyrentBookingCalendarOpen,
} from 'shared/selectors/dailyrentBookingCalendar';
import { trackCalendarDatesSubmit } from 'shared/tracking/dailyrentBookingCalendar';

import { ICalendarContainerProps } from '../../../../packages/Calendar/shared/containers/CalendarContainer/types';
import {
  getNormalizedEndDate,
  isDayAvailable,
  getValidationErrorMessage,
  formatDate,
  updateUrlWithCheckInAndCheckOut,
  getNormalizedInitialDates,
} from '../helpers';

export const usePreparedBookingCalendarData = (): ICalendarContainerProps => {
  const open = useSelector(selectDailyrentBookingCalendarOpen);
  const daysAvailability = useSelector(selectDailyrentBookingCalendarDaysAvailability);
  const dates = useSelector(selectDailyrentBookingCalendarDates);
  const initialLoading = useSelector(selectDailyrentBookingCalendarInitialLoading);
  const availabilityLoading = useSelector(selectDailyrentBookingCalendarAvailabilityLoading);
  const offerId = useSelector(selectOfferId);

  const loading = initialLoading || availabilityLoading;

  const dispatch = useDispatch();
  const [minDate] = React.useState(new Date());
  const maxDate = React.useMemo(
    () => addDays(minDate, daysAvailability.length || 365),
    [daysAvailability.length, minDate],
  );

  const { normalizedInitialStart, normalizedInitialEnd } = getNormalizedInitialDates(minDate, dates, daysAvailability);
  const [start, setStartDate] = React.useState<Date | null>(normalizedInitialStart);
  const [end, setEndDate] = React.useState<Date | null>(normalizedInitialEnd);

  React.useEffect(() => {
    if (open) {
      const { normalizedInitialStart, normalizedInitialEnd } = getNormalizedInitialDates(
        minDate,
        dates,
        daysAvailability,
      );

      setStartDate(normalizedInitialStart);
      setEndDate(normalizedInitialEnd);
    }
  }, [open]);

  const isDayDisabled = React.useCallback((day: Date) => !isDayAvailable(daysAvailability, day), [daysAvailability]);

  const onSubmit = React.useCallback(
    async (start: Date | null, end: Date | null) => {
      if (!start || !end) {
        return;
      }

      const formattedFrom = formatDate(start);
      const formattedTo = formatDate(end);

      const newUrl = updateUrlWithCheckInAndCheckOut(window.location.href, {
        checkin: formattedFrom,
        checkout: formattedTo,
      });

      const errorMessage = await dispatch(checkAvailability({ dates: { start: formattedFrom, end: formattedTo } }));

      if (errorMessage) {
        return;
      }

      history.replaceState(undefined, '', newUrl);
      dispatch(toggleCalendarModal(false));
      dispatch(setDates({ from: formattedFrom, to: formattedTo }));
      dispatch(setCanBeBooked(true));
      trackCalendarDatesSubmit(offerId);
    },
    [offerId],
  );

  const onChange = React.useCallback(
    (startDate: Date | undefined, endDate: Date | undefined, selectedDatesCount: number) => {
      dispatch(setError(''));

      if (!startDate || !endDate) {
        return;
      }

      const normalizedEndDate = getNormalizedEndDate(minDate, startDate, endDate, daysAvailability);

      setStartDate(startDate);
      setEndDate(normalizedEndDate);

      const areBothDatesSelected = selectedDatesCount === 2;
      if (!areBothDatesSelected) {
        return;
      }

      const error = getValidationErrorMessage({ fromValue: startDate, toValue: normalizedEndDate });

      if (error) {
        return dispatch(setError(error));
      }

      return onSubmit(startDate, normalizedEndDate);
    },
    [daysAvailability, minDate, onSubmit],
  );

  return React.useMemo(
    () => ({
      minDate,
      maxDate,
      onChange,
      isDayDisabled,
      loading,
      fromValue: start,
      toValue: end,
    }),
    [minDate, maxDate, onChange, isDayDisabled, loading, start, end],
  );
};
